import { isRouteErrorResponse, Links, Meta, Outlet, Scripts, ScrollRestoration } from "react-router";
import React from "react";

import type { Route } from "./+types/root";

import { useInitializeUser } from "~/lib/stores/user";
import { useNavigationMonitor } from "~/lib/useNavigationMonitor";
import "~/types/global";
import "./app.css";
import logoIco from "~/assets/logo.ico";
import { envName, isDevelopment, isProduction } from "~/lib/env";
import { gtmScript, gtmNoscriptUrl } from "./data/gtm";
import * as Sentry from "@sentry/react";

if (!isDevelopment) {
  Sentry.init({
    dsn: "https://027f21b117578db4c29e4dcec658db4d@sentry.blidz.com/2",
    // Setting this option to true will send default PII data to Sentry.
    // For example, automatic IP address collection on events
    sendDefaultPii: true,
    environment: envName(),
    integrations: [Sentry.browserTracingIntegration(), Sentry.replayIntegration()],
    // Tracing
    tracesSampleRate: 1.0,
    // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
    tracePropagationTargets: [
      "localhost",
      import.meta.env.VITE_API_BASE_URL || import.meta.env.VITE_PROXY_API_BASE_URL,
    ],
    // Session Replay
    replaysSessionSampleRate: isProduction ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Add this script to load fonts asynchronously
const fontLoadScript = `
  (function() {
    var link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Figtree:ital,wght@0,300..900;1,300..900&display=swap';
    document.head.appendChild(link);
  })();
`;

// AppsFlyer Smart Script is loaded from external file for better caching

export const meta: Route.MetaFunction = () => {
  return [
    {
      property: "og:image",
      content: "https://asset.blidz.com/blidz-cdn/images/common/generic_share_20251015.jpg",
    },
    { property: "og:title", content: "Blidz | Free Deals & Cash Payouts" },
    {
      property: "og:description",
      content:
        "Earn real money when shopping! Withdraw to PayPal & gift cards. Team-Buy with friends to secure deals up to 90% OFF or for FREE.",
    },
  ];
};

export const links: Route.LinksFunction = () => {
  const preconnectLinks: Route.LinkDescriptors = [
    // fonts
    {
      rel: "preconnect",
      href: "https://fonts.googleapis.com",
    },
    {
      rel: "preconnect",
      href: "https://fonts.gstatic.com",
      crossOrigin: "anonymous",
    },
  ];

  // Add API base URL preconnect if available
  if (import.meta.env.VITE_API_BASE_URL) {
    preconnectLinks.push({
      rel: "preconnect",
      href: import.meta.env.VITE_API_BASE_URL,
    });
  }

  // Add CDN host preconnect if available
  if (import.meta.env.VITE_TENCENT_CDN_HOST) {
    preconnectLinks.push({
      rel: "preconnect",
      href: `https://${import.meta.env.VITE_TENCENT_CDN_HOST}`,
    });
  }

  // Add favicon links
  const faviconLinks = [
    {
      rel: "icon",
      type: "image/x-icon",
      href: logoIco,
    },
  ];

  return [...preconnectLinks, ...faviconLinks];
};

export function Layout({ children }: { children: React.ReactNode }) {
  // Initialize user authentication on app start
  useInitializeUser();

  return (
    <html lang="en">
      <head>
        <title>Blidz</title>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />

        {/* pinterest verification */}
        <meta name="p:domain_verify" content="d4b1a218b544b599c1af89a76579065a" />

        <Meta />
        <Links />

        {/* Google Tag Manager */}
        <script
          dangerouslySetInnerHTML={{
            __html: gtmScript,
          }}
        />
        {/* End Google Tag Manager */}

        <script dangerouslySetInnerHTML={{ __html: fontLoadScript }} />
      </head>
      <body>
        {/* Google Tag Manager (noscript) */}
        <noscript>
          <iframe src={gtmNoscriptUrl} height={0} width={0} style={{ display: "none", visibility: "hidden" }} />
        </noscript>
        {/* Google Tag Manager (noscript) */}
        <main>{children}</main>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function App() {
  // Monitor browser navigation events across all pages
  useNavigationMonitor();

  return <Outlet />;
}

export default isDevelopment ? App : Sentry.withProfiler(App);

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  // Capture error in Sentry with context
  if (!isDevelopment) {
    Sentry.captureException(error, {
      tags: {
        component: "ErrorBoundary",
        location: "root",
      },
      extra: {
        isRouteError: isRouteErrorResponse(error),
        url: window.location.href,
      },
    });
  }

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details = error.status === 404 ? "The requested page could not be found." : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="container mx-auto p-4 pt-16">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full overflow-x-auto p-4">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
